"use client";

import * as React from "react";
import { Popover as PopoverPrimitive } from "radix-ui";
import { domains } from "@smastrom/react-email-autocomplete";

import { EMAIL_BASE_LIST } from "@/lib/email-domains";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type EmailInputProps = Omit<
  React.ComponentProps<"input">,
  "value" | "onChange" | "type" | "defaultValue"
> & {
  value: string | undefined;
  onChange: (value: string) => void;
  onSelectSuggestion?: (value: string) => void;
  baseList?: readonly string[];
  refineList?: readonly string[];
  minChars?: number;
  maxResults?: number;
  dropdownClassName?: string;
  suggestionClassName?: string;
};

function buildSuggestions(
  value: string,
  baseList: readonly string[],
  refineList: readonly string[],
  minChars: number,
  maxResults: number,
): string[] {
  const raw = value.trim();
  if (!raw || /\s/.test(raw)) return [];
  const at = raw.indexOf("@");
  if (at === -1) {
    if (raw.length < minChars) return [];
    return baseList.slice(0, maxResults).map((domain) => `${raw}@${domain}`);
  }
  const username = raw.slice(0, at);
  const typedDomain = raw.slice(at + 1).toLowerCase();
  if (!username || typedDomain.includes("@")) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const domain of [...baseList, ...refineList]) {
    if (result.length >= maxResults) break;
    if (seen.has(domain)) continue;
    seen.add(domain);
    if (domain === typedDomain || !domain.startsWith(typedDomain)) continue;
    result.push(`${username}@${domain}`);
  }
  return result;
}

function EmailInput({
  value,
  onChange,
  onSelectSuggestion,
  baseList = EMAIL_BASE_LIST,
  refineList = domains,
  minChars = 2,
  maxResults = 6,
  className,
  dropdownClassName,
  suggestionClassName,
  onFocus,
  onBlur,
  onKeyDown,
  ref,
  id,
  ...props
}: EmailInputProps) {
  const { t } = useViewerLanguage();
  const generatedId = React.useId();
  const listboxId = `${id ?? generatedId}-suggestions`;
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);

  const currentValue = value ?? "";
  const suggestions = React.useMemo(
    () =>
      buildSuggestions(currentValue, baseList, refineList, minChars, maxResults),
    [currentValue, baseList, refineList, minChars, maxResults],
  );
  const open = focused && !dismissed && suggestions.length > 0;

  const setRefs = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  const select = (suggestion: string) => {
    onChange(suggestion);
    onSelectSuggestion?.(suggestion);
    setDismissed(true);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (open) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % suggestions.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((index) =>
          index <= 0 ? suggestions.length - 1 : index - 1,
        );
        return;
      }
      if (event.key === "Enter" && activeIndex >= 0) {
        event.preventDefault();
        select(suggestions[activeIndex]);
        return;
      }
    }
    onKeyDown?.(event);
  };

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) setDismissed(true);
      }}
    >
      <PopoverPrimitive.Anchor asChild>
        <input
          {...props}
          id={id}
          ref={setRefs}
          type="email"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={
            open && activeIndex >= 0
              ? `${listboxId}-${activeIndex}`
              : undefined
          }
          value={currentValue}
          className={className}
          onChange={(event) => {
            onChange(event.target.value);
            setDismissed(false);
            setActiveIndex(-1);
          }}
          onFocus={(event) => {
            setFocused(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setFocused(false);
            setDismissed(false);
            setActiveIndex(-1);
            onBlur?.(event);
          }}
          onKeyDown={handleKeyDown}
        />
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            const target = event.target as Node | null;
            if (target && inputRef.current?.contains(target)) {
              event.preventDefault();
            }
          }}
          className={cn(
            "z-[60] max-h-(--radix-popover-content-available-height) w-(--radix-popover-trigger-width) overflow-y-auto overscroll-contain rounded-md border border-border bg-popover py-1 text-xs text-popover-foreground shadow-md outline-hidden",
            dropdownClassName,
          )}
        >
          <ul id={listboxId} role="listbox" aria-label={t("Email suggestions")}>
            {suggestions.map((suggestion, index) => {
              const at = suggestion.indexOf("@");
              return (
                <li
                  key={suggestion}
                  id={`${listboxId}-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  data-active={index === activeIndex ? "" : undefined}
                  onMouseDown={(event) => event.preventDefault()}
                  onPointerMove={() => setActiveIndex(index)}
                  onClick={() => select(suggestion)}
                  className={cn(
                    "cursor-pointer truncate px-2 py-1.5 data-active:bg-accent data-active:text-accent-foreground",
                    suggestionClassName,
                  )}
                >
                  <span>{suggestion.slice(0, at)}</span>
                  <span className="text-muted-foreground">
                    {suggestion.slice(at)}
                  </span>
                </li>
              );
            })}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export { EmailInput };
export type { EmailInputProps };
